import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaComment, FaRetweet, FaBookmark, FaShare, FaFacebook, FaInstagram, FaLinkedin, FaPaperPlane, FaMusic, FaYoutube, FaRegHeart, FaRegComment, FaRegBookmark, FaQuoteLeft, FaEllipsisH, FaTrash, FaFlag, FaBan, FaTrophy, FaEdit, FaLock, FaCrown } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import VerificationBadge from '../VerificationBadge';
import MusicTag from '../MusicTag';
import AudioPlayer from '../AudioPlayer';
import LinkPreview from '../LinkPreview';
import TipButton from './TipButton';
import { formatTimeAgo, parseContent, reactions } from './feedUtils';

const platformIcons = {
  twitter: <FaXTwitter className="text-black" />,
  facebook: <FaFacebook className="text-[#1877F2]" />,
  instagram: <FaInstagram className="text-[#E4405F]" />,
  linkedin: <FaLinkedin className="text-[#0A66C2]" />,
  tiktok: <FaMusic className="text-[#000000]" />,
  youtube: <FaYoutube className="text-[#FF0000]" />,
};

const PostCard = ({
  post, currentUser, likedPosts, bookmarkedPosts, followedUsers,
  onLike, onReaction, onRepost, onBookmark, onQuote, onComment,
  onDelete, onReport, onBlock, onFollow, onEdit,
  expandedComments, comments, newComment, onNewCommentChange, onToggleComments,
}) => {
  const [showReactions, setShowReactions] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  // Close menu on outside click
  React.useEffect(() => {
    if (!openMenu) return;
    const handleClick = () => setOpenMenu(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openMenu]);

  const isLiked = likedPosts.has(post.id);
  const isBookmarked = bookmarkedPosts.has(post.id);
  const isFollowed = followedUsers.has(post.user_id);
  const isMine = post.user_id === currentUser.id;
  const isExpanded = expandedComments[post.id];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-all ${
        post.is_potd_winner
          ? 'border-2 border-amber-300 ring-2 ring-amber-100 shadow-lg shadow-amber-100/50'
          : 'border border-gray-200'
      }`}
      data-testid={`post-${post.id}`}
    >
      {/* POTD Winner Golden Header */}
      {post.is_potd_winner && (
        <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 px-5 py-2 flex items-center space-x-2">
          <FaTrophy className="text-white" size={13} />
          <span className="text-xs font-bold text-white uppercase tracking-wider font-arthaus">Point of the Day</span>
          {post.potd_votes > 0 && <span className="text-xs bg-white/30 text-white px-2 py-0.5 rounded-full font-bold">{post.potd_votes} votes</span>}
        </div>
      )}
      {post.is_repost && (
        <div className="px-6 pt-3 flex items-center space-x-2 text-gray-500 text-sm">
          <FaRetweet /><span>{post.reposted_by_name} reposted</span>
        </div>
      )}
      {post.quoted_post && (
        <div className="px-6 pt-3 flex items-center space-x-2 text-gray-500 text-sm">
          <FaQuoteLeft /><span>Quote Post</span>
        </div>
      )}

      <div className="p-4 sm:p-6">
        {/* Post Header */}
        <div className="flex items-start space-x-3 mb-4">
          <Link to={`/profile/${post.username}`} className="flex-shrink-0">
            <img src={post.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt={post.full_name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover hover:ring-2 hover:ring-[#4A90E2] transition-all" />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <Link to={`/profile/${post.username}`} className="hover:underline min-w-0">
                    <h3 className="text-gray-900 font-bold text-sm sm:text-base truncate">{post.full_name}</h3>
                  </Link>
                  {post.is_verified && <VerificationBadge badgeType={post.verification_badge} size="sm" />}
                  {post.is_potd_winner && <span className="inline-flex items-center flex-shrink-0" title="Point of the Day Winner"><FaTrophy className="text-amber-500 animate-pulse" size={11} /></span>}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span className="truncate">@{post.username}</span>
                  <span className="flex-shrink-0">·</span>
                  <span className="flex-shrink-0">{formatTimeAgo(post.created_at)}</span>
                  {post.is_edited && <span className="text-gray-300 italic flex-shrink-0">(edited)</span>}
                </div>
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                {!isMine && (
                  <button onClick={() => onFollow(post.user_id)} data-testid={`follow-btn-${post.id}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${isFollowed ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-gray-700'}`}>
                    {isFollowed ? 'Following' : 'Follow'}
                  </button>
                )}
                <div className="relative">
                  <button onClick={() => setOpenMenu(v => !v)} className="text-gray-400 hover:text-gray-600 p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" data-testid={`post-menu-${post.id}`}>
                    <FaEllipsisH size={14} />
                  </button>
                  {openMenu && (
                    <div className="absolute right-0 top-9 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 min-w-[140px]">
                      {isMine && (
                        <>
                          <button onClick={() => { setOpenMenu(false); onEdit(post); }} data-testid={`edit-post-${post.id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"><FaEdit size={12} /><span>Edit</span></button>
                          <button onClick={() => { setOpenMenu(false); onDelete(post.id); }} data-testid={`delete-post-${post.id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"><FaTrash size={12} /><span>Delete</span></button>
                        </>
                      )}
                      {!isMine && (
                        <>
                          <button onClick={() => { setOpenMenu(false); onReport(post.id); }} data-testid={`report-post-${post.id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"><FaFlag size={12} /><span>Report</span></button>
                          <button onClick={() => { setOpenMenu(false); onBlock(post.user_id); }} data-testid={`block-user-${post.user_id}`} className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"><FaBan size={12} /><span>Block User</span></button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {/* Promoted badge */}
        {post.is_promoted && (
          <div className="flex items-center gap-1.5 mb-3 text-xs text-gray-400">
            <span className="bg-gray-100 px-2 py-0.5 rounded-full font-medium">Promoted</span>
            {post.sponsor_name && <span>by {post.sponsor_name}</span>}
          </div>
        )}

        {/* Exclusive content gate */}
        {post.is_exclusive && !isMine && !post.has_access ? (
          <div className="relative mb-4">
            <div className="text-gray-900 leading-relaxed whitespace-pre-wrap line-clamp-2 blur-sm select-none">{post.content}</div>
            {post.media_url && <div className="mt-2 rounded-xl overflow-hidden h-48 bg-gray-100 blur-md" />}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                <FaLock className="text-amber-500" size={18} />
              </div>
              <p className="text-sm font-bold text-gray-900">Subscriber-only content</p>
              <p className="text-xs text-gray-500 mt-1 mb-3">Subscribe to @{post.username} to unlock</p>
              <Link to={`/profile/${post.username}`} className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full hover:shadow-lg transition-all">
                <FaCrown className="inline mr-1" size={10} />Subscribe
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="text-gray-900 leading-relaxed mb-4 whitespace-pre-wrap">
              {post.is_exclusive && <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-medium mr-2 mb-1"><FaLock size={8} />Exclusive</span>}
              {parseContent(post.content)}
            </div>

        {/* Media */}
        {post.media_url && (
          <div className="mb-4 rounded-xl overflow-hidden">
            {post.media_type === 'audio' ? (
              <AudioPlayer audioUrl={post.media_url} fileName={post.content !== 'Audio post' ? undefined : 'Voice note'} />
            ) : post.media_type?.startsWith('image') ? (
              <img src={post.media_url} alt="Post media" loading="lazy" className="w-full object-cover max-h-[500px]" />
            ) : post.media_type?.startsWith('video') ? (
              <video src={post.media_url} controls className="w-full max-h-[500px]" />
            ) : null}
          </div>
        )}

        {post.link_preview && <div className="mb-4"><LinkPreview linkData={post.link_preview} /></div>}

        {/* Quoted Post */}
        {post.quoted_post && (
          <div className="border border-gray-200 rounded-xl p-4 mb-4 hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center space-x-2 mb-2">
              <Link to={`/profile/${post.quoted_post.username}`}><img src={post.quoted_post.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt={post.quoted_post.full_name} className="w-6 h-6 rounded-full object-cover hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" /></Link>
              <Link to={`/profile/${post.quoted_post.username}`} className="hover:underline"><span className="font-medium text-sm">{post.quoted_post.full_name}</span></Link>
              <span className="text-gray-500 text-sm">@{post.quoted_post.username}</span>
            </div>
            <p className="text-gray-700 text-sm line-clamp-3">{post.quoted_post.content}</p>
          </div>
        )}

        {post.platforms?.length > 0 && (
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-gray-500 text-xs">Cross-posted to:</span>
            <div className="flex space-x-1">{post.platforms.map(p => <div key={p} className="text-sm">{platformIcons[p]}</div>)}</div>
          </div>
        )}

        {post.music?.song_id && <MusicTag music={post.music} />}
          </>
        )}

        {/* Promoted CTA */}
        {post.is_promoted && post.cta_text && post.cta_url && (
          <a href={post.cta_url} target="_blank" rel="noopener noreferrer" className="block mb-4 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl text-center text-sm font-semibold text-[#4A90E2] transition-colors" data-testid={`promoted-cta-${post.id}`}>
            {post.cta_text}
          </a>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="relative">
            <button data-testid={`like-btn-${post.id}`} onClick={() => onLike(post.id)} onMouseEnter={() => setShowReactions(true)} className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all ${isLiked ? 'text-red-500 bg-red-50' : 'text-gray-500 hover:text-red-500 hover:bg-red-50'}`}>
              {isLiked ? <FaHeart /> : <FaRegHeart />}
              <span className="text-sm font-medium">{post.likes_count}</span>
            </button>
            <AnimatePresence>
              {showReactions && (
                <motion.div initial={{ opacity: 0, y: 10, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.9 }} onMouseLeave={() => setShowReactions(false)} className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-full shadow-lg px-2 py-1 flex space-x-1">
                  {reactions.map(r => <button key={r.type} onClick={() => { onReaction(post.id, r.type); setShowReactions(false); }} className="text-2xl hover:scale-125 transition-transform p-1" title={r.label}>{r.icon}</button>)}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button onClick={() => onToggleComments(post.id)} className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all ${isExpanded ? 'text-[#4A90E2] bg-blue-50' : 'text-gray-500 hover:text-[#4A90E2] hover:bg-blue-50'}`}>
            {isExpanded ? <FaComment /> : <FaRegComment />}
            <span className="text-sm font-medium">{post.comments_count}</span>
          </button>
          <button onClick={() => onRepost(post.id)} className="flex items-center space-x-2 px-3 py-2 rounded-full text-gray-500 hover:text-green-500 hover:bg-green-50 transition-all"><FaRetweet /><span className="text-sm font-medium">{post.shares_count}</span></button>
          <button onClick={() => onQuote(post)} className="flex items-center space-x-2 px-3 py-2 rounded-full text-gray-500 hover:text-purple-500 hover:bg-purple-50 transition-all"><FaQuoteLeft /></button>
          <TipButton recipientId={post.user_id} targetType="post" targetId={post.id} currentUserId={currentUser.id} />
          <button onClick={() => onBookmark(post.id)} className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all ${isBookmarked ? 'text-yellow-500 bg-yellow-50' : 'text-gray-500 hover:text-yellow-500 hover:bg-yellow-50'}`}>{isBookmarked ? <FaBookmark /> : <FaRegBookmark />}</button>
          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`); toast.success('Link copied!'); }} className="flex items-center space-x-2 px-3 py-2 rounded-full text-gray-500 hover:text-[#4A90E2] hover:bg-blue-50 transition-all"><FaShare /></button>
        </div>

        {/* Comments Section */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              <div className="flex space-x-3">
                <input type="text" value={newComment[post.id] || ''} onChange={e => onNewCommentChange(post.id, e.target.value)} onKeyPress={e => e.key === 'Enter' && onComment(post.id)} placeholder="Write a comment..." className="flex-1 bg-gray-100 border-none rounded-full px-4 py-2.5 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/50" />
                <button onClick={() => onComment(post.id)} className="bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white rounded-full px-4 py-2.5 transition-all hover:opacity-90"><FaPaperPlane /></button>
              </div>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {comments[post.id]?.map(c => (
                  <div key={c.id} className="flex space-x-3">
                    <Link to={`/profile/${c.username}`}><img src={c.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt={c.full_name} className="w-8 h-8 rounded-full object-cover" /></Link>
                    <div className="flex-1 bg-gray-50 rounded-2xl px-4 py-2.5">
                      <div className="flex items-center space-x-2 mb-0.5">
                        <Link to={`/profile/${c.username}`} className="font-semibold text-sm text-gray-900 hover:underline">{c.full_name}</Link>
                        <span className="text-gray-400 text-xs">@{c.username}</span>
                      </div>
                      <p className="text-gray-700 text-sm">{parseContent(c.content)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default PostCard;
